"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui/button";
import { ViewModeToggle } from "@/components/ViewModeToggle";
import {
  Download,
  Settings,
  Sparkles,
  Cloud,
  Calendar,
} from "lucide-react";
import { useSettings } from "@/components/SettingsContext";
import { AIQuickActions } from "@/components/templates/AIQuickActions";
import { AI_ENABLED } from "@/lib/ai-config";

import { useSubscription } from "@/components/SubscriptionContext";
import { ResetButton } from "@/components/ResetButton";
import { useAuth } from "@clerk/nextjs";
import { type Event } from "@/lib/types";
import { useTranslations } from "next-intl";

const AuthModal = dynamic(
  () => import("@/components/AuthModal").then((m) => m.AuthModal),
  { ssr: false },
);
const SettingsDialog = dynamic(
  () => import("@/components/SettingsDialog").then((m) => m.SettingsDialog),
  { ssr: false },
);
const CloudSaveDialog = dynamic(
  () => import("@/components/CloudSaveDialog").then((m) => m.CloudSaveDialog),
  { ssr: false },
);
const CalendarSyncDialog = dynamic(
  () =>
    import("@/components/CalendarSyncDialog").then((m) => m.CalendarSyncDialog),
  { ssr: false },
);
const AIAutofillDialog = dynamic(
  () => import("@/components/AIAutofillDialog").then((m) => m.AIAutofillDialog),
  { ssr: false },
);

interface SidebarProps {
  events: Event[];
  onReset: () => void;
  viewMode: "day" | "week";
  onViewModeChange: (mode: "day" | "week") => void;
  weekStart: Date;
  weekStartsOnSunday: boolean;
  onExport: () => void;
  showSettingsOpen?: boolean;
  onSettingsOpenChange?: (open: boolean) => void;
  onLoadSchedule?: (
    events: Event[],
    settings: Record<string, unknown> | null,
  ) => void;
  onAddEvents?: (events: Omit<Event, "id">[]) => void;
  showAIAutofillOpen?: boolean;
  onAIAutofillOpenChange?: (open: boolean) => void;
}

export function Sidebar({
  events,
  onReset,
  viewMode,
  onViewModeChange,
  weekStart,
  weekStartsOnSunday,
  onExport,
  showSettingsOpen,
  onSettingsOpenChange,
  onLoadSchedule,
  onAddEvents,
  showAIAutofillOpen,
  onAIAutofillOpenChange,
}: SidebarProps) {
  const t = useTranslations("Common");
  const { userId } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  // Sync external state if provided
  const effectiveShowSettings =
    showSettingsOpen !== undefined ? showSettingsOpen : showSettingsDialog;
  const setEffectiveSettingsOpen = (open: boolean) => {
    if (onSettingsOpenChange) {
      onSettingsOpenChange(open);
    } else {
      setShowSettingsDialog(open);
    }
  };
  const [showCloudSaveDialog, setShowCloudSaveDialog] = useState(false);
  const [showCalendarSyncDialog, setShowCalendarSyncDialog] = useState(false);
  const [showAIAutofillDialog, setShowAIAutofillDialog] = useState(false);

  // Sync external AI Autofill state if provided
  const effectiveShowAIAutofill =
    showAIAutofillOpen !== undefined
      ? showAIAutofillOpen
      : showAIAutofillDialog;
  const setEffectiveAIAutofillOpen = (open: boolean) => {
    if (onAIAutofillOpenChange) {
      onAIAutofillOpenChange(open);
    } else {
      setShowAIAutofillDialog(open);
    }
  };
  const { isLoading } = useSubscription();
  const { settings } = useSettings();

  const handleExportClick = () => {
    // Open export dialog in parent
    onExport();
  };

  const handleAIAutofillClick = () => {
    if (isLoading) return;

    // Requirement: Unauthenticated users see login prompt
    if (!userId) {
      setShowAuthModal(true);
      return;
    }

    // Authenticated users (Free or Pro) open the dialog
    // The dialog itself will handle tiered usage and trial paywalls
    setEffectiveAIAutofillOpen(true);
  };

  const handleCloudSaveClick = () => {
    if (isLoading) return;

    // Requirement: Unauthenticated users see login prompt
    if (!userId) {
      setShowAuthModal(true);
      return;
    }

    // Authenticated users (Free or Pro) open the cloud save dialog
    // The dialog itself will handle tiered storage limits
    setShowCloudSaveDialog(true);
  };

  const handleCalendarSyncClick = () => {
    // Requirement: Unauthenticated users see login prompt
    if (!userId) {
      setShowAuthModal(true);
      return;
    }

    // All users (Free and Pro) can use Calendar Sync
    setShowCalendarSyncDialog(true);
  };

  return (
    <aside className="hidden md:flex w-[230px] h-full shrink-0 flex-col border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950 p-4 overflow-hidden">
      {/* Day/Week Toggle */}
      <ViewModeToggle
        value={viewMode}
        onValueChange={onViewModeChange}
        className="w-full mb-3"
      />

      {/* AI Autofill Button with shimmer */}
      {AI_ENABLED && (
        <Button
          className="w-full mb-3 bg-gradient-to-r from-violet-500 to-blue-500 hover:from-violet-600 hover:to-blue-600 text-white gap-2 shimmer hover:glow-primary transition-shadow duration-300"
          onClick={handleAIAutofillClick}
        >
          <Sparkles className="size-5" />
          {t("sidebar.aiAutofill")}
        </Button>
      )}

      {/* Menu Items */}
      <nav className="flex flex-col gap-1">
        {/* Reset Button */}
        <ResetButton events={events} onReset={onReset} />

        {/* Cloud Save */}
        <Button
          variant="ghost"
          className="justify-start gap-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          onClick={handleCloudSaveClick}
        >
          <Cloud className="size-5" />
          {t("sidebar.cloudSave")}
        </Button>

        {/* Cloud Save Dialog */}
        <CloudSaveDialog
          open={showCloudSaveDialog}
          onOpenChange={setShowCloudSaveDialog}
          onLoadSchedule={(events, settings) => {
            if (onLoadSchedule) {
              onLoadSchedule(events, settings);
            }
          }}
        />

        {/* Calendar Sync */}
        <Button
          variant="ghost"
          className="justify-start gap-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          onClick={handleCalendarSyncClick}
        >
          <Calendar className="size-5" />
          {t("sidebar.calendarSync")}
        </Button>

        {/* Calendar Sync Dialog */}
        <CalendarSyncDialog
          open={showCalendarSyncDialog}
          onOpenChange={setShowCalendarSyncDialog}
          weekStart={weekStart}
          weekStartsOnSunday={weekStartsOnSunday}
        />

        {/* Export/Download */}
        <Button
          variant="ghost"
          className="justify-start gap-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          onClick={handleExportClick}
        >
          <Download className="size-5" />
          {t("sidebar.exportDownload")}
        </Button>

        {/* Settings */}
        <Button
          variant="ghost"
          className="justify-start gap-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          onClick={() => setEffectiveSettingsOpen(true)}
        >
          <Settings className="size-5" />
          {t("sidebar.settings")}
        </Button>

        <div className="h-px bg-gray-100 my-2 mx-1 dark:bg-gray-800" />

        {/* AI Quick Actions - Only for AI Schedule Builder Template */}
        {AI_ENABLED &&
          settings.activeTemplateSlug === "ai-schedule-builder" && (
            <AIQuickActions
              onRegenerateSchedule={() => setEffectiveAIAutofillOpen(true)}
              onClearSchedule={onReset}
            />
          )}

        {/* Settings Dialog */}
        <SettingsDialog
          open={effectiveShowSettings}
          onOpenChange={setEffectiveSettingsOpen}
        />
      </nav>

      {AI_ENABLED && (
        <AIAutofillDialog
          open={effectiveShowAIAutofill}
          onOpenChange={setEffectiveAIAutofillOpen}
          onAddEvents={(events) => {
            if (onAddEvents) {
              onAddEvents(events);
            }
          }}
          weekStart={weekStart}
          weekStartsOnSunday={weekStartsOnSunday}
          onReset={onReset}
        />
      )}

      {/* Auth Modal */}
      <AuthModal
        open={showAuthModal}
        onOpenChange={setShowAuthModal}
        defaultMode="sign-up"
      />
    </aside>
  );
}
